import { Link, useLocation } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export type AdminTabKey =
  | "overview"
  | "leaderboard"
  | "funnel"
  | "health"
  | "reps"
  | "deals"
  | "invoices"
  | "customers"
  | "commissions"
  | "payouts"
  | "reconcile"
  | "products"
  | "fx"
  | "sync"
  | "webhooks"
  | "audit";

const tabs: { key: AdminTabKey; href: string; label: string }[] = [
  { key: "overview", href: "/admin", label: "Overview" },
  { key: "leaderboard", href: "/admin/leaderboard", label: "Leaderboard" },
  { key: "funnel", href: "/admin/funnel", label: "Funnel" },
  { key: "health", href: "/admin/health", label: "Health" },
  { key: "reps", href: "/admin/reps", label: "Reps" },
  { key: "deals", href: "/admin/deals", label: "Deals" },
  { key: "customers", href: "/admin/customers", label: "Customers" },
  { key: "invoices", href: "/admin/invoices", label: "Invoices" },
  { key: "commissions", href: "/admin/commissions", label: "Commissions" },
  { key: "payouts", href: "/admin/payouts", label: "Payouts" },
  { key: "reconcile", href: "/admin/reconcile", label: "Reconcile" },
  { key: "products", href: "/admin/products", label: "Products" },
  { key: "fx", href: "/admin/fx", label: "FX" },
  { key: "sync", href: "/admin/sync", label: "Sync" },
  { key: "webhooks", href: "/admin/webhooks", label: "Webhooks" },
  { key: "audit", href: "/admin/audit", label: "Audit" },
];

export function AdminTabs() {
  const { pathname } = useLocation();
  const active = resolveActive(pathname);

  // Shares the query cache with the overview page (same key) so this stays a
  // single fetch. Only the failed-webhook count is read here.
  const { data: overview } = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => api.get<{ counts: { failedWebhooks: number } }>("/api/admin/overview"),
    staleTime: 60_000,
  });
  const failedWebhooks = overview?.counts.failedWebhooks ?? 0;

  return (
    <nav className="tabs" aria-label="Admin sections">
      {tabs.map((t) => (
        <Link key={t.key} to={t.href} className={t.key === active ? "active" : ""} aria-current={t.key === active ? "page" : undefined}>
          {t.label}
          {t.key === "webhooks" && failedWebhooks > 0 && (
            <span
              aria-label={`${failedWebhooks} failed webhook${failedWebhooks === 1 ? "" : "s"}`}
              style={{
                marginLeft: 6,
                padding: "1px 6px",
                borderRadius: 999,
                background: "var(--err)",
                color: "#fff",
                fontSize: 10.5,
                fontWeight: 600,
                lineHeight: "14px",
                display: "inline-block",
                verticalAlign: "middle",
              }}
            >
              {failedWebhooks}
            </span>
          )}
        </Link>
      ))}
    </nav>
  );
}

function resolveActive(pathname: string): AdminTabKey | null {
  if (pathname === "/admin") return "overview";
  // Order matters — match the longest prefix first so /admin/commissions/123
  // still resolves to "commissions".
  const ordered: AdminTabKey[] = [
    "leaderboard",
    "funnel",
    "health",
    "reps",
    "deals",
    "invoices",
    "customers",
    "commissions",
    "payouts",
    "reconcile",
    "products",
    "fx",
    "sync",
    "webhooks",
    "audit",
  ];
  for (const key of ordered) {
    if (pathname.startsWith(`/admin/${key}`)) return key;
  }
  return null;
}
